import { Button, Checkbox, Input, Modal, notification, Text } from "@findemdev/maui";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
// api
import { useCreateCompanyCompMacrosMutation } from "../../../../../services/searches";
import { useCreateSyncedCompanyListsMutation } from "../../../../../services/company";
// context
import UserContext from "../../../../../contexts/UserContext";
// types
import { ICompanyList, RecencyType } from "../../../types/common";
import { ICreateCompanyCompMacrosPayload } from "../../../types/api";
// Component + styles
import {
  Body,
  Description,
  DynamicListHint,
  DynamicListSection,
  ListName,
} from "./CreateCustomListsModal.styled";
// hooks
import { useHasCompanyListEditAccess } from "../../../hooks/useHasCompanyListEditAccess";
// constant
import { CompanyBuilder } from "../../../../../constants/namingConventions";
import { decodeString } from "@findemdev/findem-helpers";

interface ICreateCustomListsModalProps {
  data: ICompanyList | undefined;
  onOk: () => Promise<void>;
  onCancel: () => void;
}

type CreateCompanyCompMacrosFn = (params: {
  company_ids: string[];
  joid: string;
  name: string;
  description: string;
  regencies: string[];
}) => ICreateCompanyCompMacrosPayload[];

const recencyToVariant: Record<string, string> = {
  [RecencyType.current]: "current",
  [RecencyType.recent]: "recent",
  [RecencyType.past]: "past",
  [RecencyType.past_and_current]: "past_or_current",
};

const createCompanyCompMacros: CreateCompanyCompMacrosFn = ({
  company_ids,
  joid,
  name,
  description,
  regencies,
}) => {
  const mids = company_ids.map((b) => {
    return "workexp.current.company.id:" + b;
  });
  const mids_recent = company_ids.map((b) => {
    return "workexp.recent.company.id:" + b;
  });
  const mids_past = company_ids.map((b) => {
    return "workexp.past.company.id:" + b;
  });
  const mids_past_or_current = company_ids.map((b) => {
    return "workexp.company.id:" + b;
  });
  const body: ICreateCompanyCompMacrosPayload = {
    joid: joid,
    negs: [],
    force_name: true,
    macroids: [],
    keywords: mids,
    op: "or",
    categories: ["Custom List"],
    ui_categories: ["Saved"],
    macro_taxonomy: ["My Data;My Lists;Company Lists;Current Company Lists"],
    description: description,
    name: name,
    is_org_public: false,
  };

  const body_recent: ICreateCompanyCompMacrosPayload = {
    joid: joid,
    negs: [],
    force_name: true,
    macroids: [],
    keywords: mids_recent,
    op: "or",
    categories: ["Custom List"],
    ui_categories: ["Saved"],
    macro_taxonomy: ["My Data;My Lists;Company Lists;Recent Company Lists"],
    description: description,
    name: name + " (Recent)",
    is_org_public: false,
  };

  const body_past: ICreateCompanyCompMacrosPayload = {
    joid: joid,
    negs: [],
    force_name: true,
    macroids: [],
    keywords: mids_past,
    op: "or",
    categories: ["Custom List"],
    ui_categories: ["Saved"],
    macro_taxonomy: ["My Data;My Lists;Company Lists;Past Company Lists"],
    description: description,
    name: name + " (Past)",
    is_org_public: false,
  };

  const body_past_or_current: ICreateCompanyCompMacrosPayload = {
    joid: joid,
    negs: [],
    force_name: true,
    macroids: [],
    keywords: mids_past_or_current,
    op: "or",
    categories: ["Custom List"],
    ui_categories: ["Saved"],
    macro_taxonomy: [
      "My Data;My Lists;Company Lists;Past or Current Company Lists",
    ],
    description: description,
    name: name + " (Past or Current)",
    is_org_public: false,
  };

  const apiPayload: ICreateCompanyCompMacrosPayload[] = [];
  if (regencies.includes(RecencyType.current)) {
    apiPayload.push(body);
  }
  if (regencies.includes(RecencyType.recent)) {
    apiPayload.push(body_recent);
  }
  if (regencies.includes(RecencyType.past)) {
    apiPayload.push(body_past);
  }
  if (regencies.includes(RecencyType.past_and_current)) {
    apiPayload.push(body_past_or_current);
  }

  return apiPayload;
};

const defaultRecencyCheckedValues = [
  RecencyType.current,
  RecencyType.recent,
  RecencyType.past,
  RecencyType.past_and_current,
];

const CreateCustomListsModal = ({
  data,
  onOk,
  onCancel,
}: ICreateCustomListsModalProps) => {
  const userModel = useContext(UserContext);
  const hasEditAccess = useHasCompanyListEditAccess(data);

  const [createCompanyCompMacro, { isLoading: isCreatingStatic }] =
    useCreateCompanyCompMacrosMutation();
  const [createSyncedCompanyLists, { isLoading: isCreatingSynced }] =
    useCreateSyncedCompanyListsMutation();

  const isLoading = isCreatingStatic || isCreatingSynced;

  const [name, setName] = useState<string>("");
  const [isDynamic, setIsDynamic] = useState<boolean>(true);
  const [checkedValues, setCheckedValues] = useState<
    (string | number | boolean)[]
  >([...defaultRecencyCheckedValues]);

  const shortlistedCompanyIds = useMemo(
    () =>
      data?.shortlist
        ?.map((value) => value.meta_ref)
        ?.filter((value) => value !== undefined) ?? [],
    [data?.shortlist]
  );

  useEffect(() => {
    setIsDynamic(hasEditAccess);
  }, [hasEditAccess]);

  const clearFormState = useCallback(() => {
    setName("");
    setIsDynamic(hasEditAccess);
    setCheckedValues([...defaultRecencyCheckedValues]);
  }, [hasEditAccess]);

  const handleSubmit = useCallback(async () => {
    try {
      if (!shortlistedCompanyIds.length) {
        return;
      }

      if (isDynamic && data?.ref) {
        const variants = (checkedValues as string[]).map(
          (recency) => recencyToVariant[recency] ?? recency
        );

        await createSyncedCompanyLists({
          ref: data.ref,
          variants,
          joid: userModel.getUser().uid,
          name,
        }).unwrap();
      } else {
        const apiPayload = createCompanyCompMacros({
          company_ids: shortlistedCompanyIds,
          joid: userModel.getUser().uid,
          description: "",
          name,
          regencies: checkedValues as string[],
        });

        const promises = apiPayload.map((payload) => {
          return createCompanyCompMacro(payload).unwrap();
        });

        await Promise.all(promises);
      }

      clearFormState();

      notification.success({
        message: "Your list was copied successfully",
        description:
          "Your list has been copied and is ready to use in other apps.",
      });
      onOk();
    } catch (error) {
      console.error("Failed to createCompanyCompMacro ", error);
      notification.error({
        message: "Failed to copy list",
      });
    }
  }, [
    onOk,
    userModel,
    checkedValues,
    name,
    data,
    isDynamic,
    shortlistedCompanyIds,
    clearFormState,
    createCompanyCompMacro,
    createSyncedCompanyLists,
  ]);

  useEffect(() => {
    if (data?.name) {
      const currentDate = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
      const newName = `Copy of ${data.name} (${currentDate})`;
      setName(decodeString(newName));
    }

    return () => {
      setName("");
    };
  }, [data?.name]);

  return (
    <Modal
      open={!!data}
      title="Make a Copy to Use in Other Apps"
      onCancel={onCancel}
      destroyOnClose={true}
      footer={[
        <Button key="close" onClick={onCancel} disabled={isLoading}>
          Close
        </Button>,
        <Button
          key="confirm"
          onClick={handleSubmit}
          disabled={
            !name || !checkedValues.length || !shortlistedCompanyIds.length
          }
          loading={isLoading}
          type="primary"
        >
          Confirm
        </Button>,
      ]}
    >
      <Body>
        <Description>
          {CompanyBuilder.modal.copyToOtherApps.description}
        </Description>
        {hasEditAccess && (
          <DynamicListSection>
            <Checkbox
              checked={isDynamic}
              onChange={(e) => setIsDynamic(e.target.checked)}
            >
              {CompanyBuilder.modal.copyToOtherApps.dynamicListLabel}
            </Checkbox>
            <DynamicListHint>
              {isDynamic
                ? CompanyBuilder.modal.copyToOtherApps.dynamicListCheckedText
                : CompanyBuilder.modal.copyToOtherApps.dynamicListUncheckedText}
            </DynamicListHint>
          </DynamicListSection>
        )}
        <ListName>
          <Text>List Name</Text>
          <Input
            defaultValue={name}
            onChange={(event) => setName(decodeString(event.target.value))}
            placeholder="Name"
          />
        </ListName>
      </Body>
    </Modal>
  );
};

export default CreateCustomListsModal;
